<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Details
            </h3>

            <p>
              Edit the details of the order, including the name and basic contact info.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-cogs"></i> Actions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li :class="{'disabled': order.data.celebrated_at}">
                  <a href="#" @click.prevent="celebrate">
                    <i class="fa fa-glass-cheers"></i> Celebrate
                  </a>
                </li>
              </ul>
            </div>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <static-form-group
                    :value="'HG' + order.data.id"
                    label="Number"
                    help-text="The number of the order."
                ></static-form-group>

                <text-form-group
                    v-model="order.data.title"
                    name="title"
                    label="Title"
                    help-text="The title of the order."
                    :error="getError('title')"
                ></text-form-group>

                <text-form-group
                    v-model="order.data.purchase_order"
                    name="purchase_order"
                    label="Purchase Order (PO number)"
                    help-text="The company's purchase order (not required)."
                    :error="getError('purchase_order')"
                ></text-form-group>

                <select-form-group
                    v-model="order.data.user_id"
                    name="user_id"
                    label="User"
                    help-text="The user that is responsible for the order."
                    :error="getError('user_id')"
                    :options="userOptions"
                ></select-form-group>

                <select-form-group
                    v-model="order.data.opportunity_id"
                    name="opportunity_id"
                    label="Opportunity"
                    help-text="The opportunity that is converted into this order."
                    :error="getError('opportunity_id')"
                    :options="opportunityOptions"
                    :nullable="true"
                ></select-form-group>

                <select-with-button-form-group
                    v-model="order.data.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="getError('contact_person_id')"
                    :options="personOptions"
                    @click="openPersonModal"
                ></select-with-button-form-group>

                <typeahead-form-group
                    v-model="order.data.content_responsible_id"
                    name="content_responsible_id"
                    label="Content Responsible"
                    help-text="The person that is responsible for the content of the ads."
                    :error="getError('content_responsible_id')"
                    endpoint="/api/v2/people"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="person => person.attributes.name + (person.attributes.company_id ? ' (' + person.attributes.company_name + ')' : '')"
                    :initial-label="order.data.content_responsible.name + (order.data.content_responsible.company ? ' (' + order.data.content_responsible.company.name + ')' : '')"
                ></typeahead-form-group>

                <typeahead-form-group
                    v-model="order.data.other_payer_id"
                    name="other_payer_id"
                    label="Other Payer"
                    help-text="The company that pays for the ad (if not the company itself)."
                    :error="getError('other_payer_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="order.data.other_payer ? order.data.other_payer.name : ''"
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <typeahead-form-group
                    v-model="order.data.other_payer_contact_person_id"
                    name="other_payer_contact_person_id"
                    label="Other Payer Contact Person"
                    help-text="The contact person in the company that pays for the ad (if not the company itself)."
                    :error="getError('other_payer_contact_person_id')"
                    endpoint="/api/v2/people"
                    :params="{company: order.data.other_payer_id}"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="order.data.other_payer_contact_person ? order.data.other_payer_contact_person.name : ''"
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <static-form-group
                    :value="agencyCommission | price"
                    label="Agency Commission"
                    help-text="The total agency commission for the order."
                ></static-form-group>

                <static-form-group
                    :value="thirdPartyCosts | price"
                    label="Third Party Costs"
                    help-text="The total third party costs for the order."
                ></static-form-group>

                <static-form-group
                    :value="order.data.created_at | datetime"
                    label="Created At"
                    help-text="The time that the order was created."
                ></static-form-group>

                <hr>

                <submit-form-group button-class="btn-black"></submit-form-group>
              </form>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Order Lines
            </h3>

            <p>
              This table displays the ads that are included in the order.
            </p>

            <router-link class="btn btn-black btn-sm" :to="orderId + '/ads/create'">
              <i class="fa fa-plus"></i> Create Order Line
            </router-link>
          </help-text>
        </div>

        <div class="col-xs-18">
          <ad-list :order="order" v-model="ads" @reload="reloadAds"></ad-list>
        </div>
      </div>

      <celebrate-order-modal :order="order" @celebrated="celebrated"></celebrate-order-modal>
    </div>

    <create-person-modal :company="company.data.id" @created="personCreated"></create-person-modal>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import store from '@/store'
import AdList from '@/pages/Companies/Companies/Edit/Panels/AdList'
import CelebrateOrderModal from '@/components/Modals/Orders/CelebrateOrderModal'
import CreatePersonModal from '@/pages/Companies/Companies/Edit/Modals/CreatePerson'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SelectWithButtonFormGroup from '@/components/FormGroups/Horizontal/SelectWithButton'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'

export default {
  components: {
    AdList,
    CelebrateOrderModal,
    CreatePersonModal,
    HelpText,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    SelectWithButtonFormGroup,
    StaticFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    TypeaheadFormGroup
  },

  mixins: [Errors, Load],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'orderId'],

  data() {
    return {
      shared: store,
      order: null,
      ads: [],
      users: [],
      opportunities: [],
      people: []
    }
  },

  mounted() {
    this.fetch()
  },

  watch: {
    'order.data.contact_person_id'() {
      this.order.data.content_responsible_id = this.order.data.contact_person_id
    },

    '$route'() {
      this.fetch()
    }
  },

  computed: {
    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    personOptions() {
      return this.people.data.map(person => {
        return {
          value: person.id,
          label: person.name
        }
      })
    },

    opportunityOptions() {
      return this.opportunities.data.map(opportunity => {
        return {
          value: opportunity.id,
          label: opportunity.attributes.user_full_name + ' - ' + this.$options.filters.date(opportunity.attributes.conversion_expected_at) + ' - ' + this.$options.filters.price(opportunity.attributes.value)
        }
      })
    },

    agencyCommission() {
      return this.ads.data.reduce((sum, ad) => sum + ad.agency_commission, 0)
    },

    thirdPartyCosts() {
      return this.ads.data.reduce((sum, ad) => sum + ad.third_party_costs, 0)
    }
  },

  methods: {
    fetch() {
      this.load({
        order: '/api/v1/orders/' + this.orderId,
        ads: '/api/v1/ads?order=' + this.orderId,
        users: '/api/v2/users',
        opportunities: '/api/v2/opportunities?filter[company_id]=' + this.company.data.id,
        people: '/api/v1/companies/' + this.company.data.id + '/people'
      })
    },

    submit() {
      http.put('/api/v1/orders/' + this.order.data.id, this.order.data)
          .then(() => {
            notify.success('The order was saved')
          })
          .catch(this.catchValidationErrors)
    },

    reloadAds() {
      this.load({ads: '/api/v1/ads?order=' + this.orderId})
    },

    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.data.push(person)

      this.people.data.sort((a, b) => a.name.toLowerCase() >= b.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.order.data.contact_person_id = person.id
      })
    },

    celebrate() {
      if (this.order.data.celebrated_at) {
        return
      }

      $('#celebrate-order-modal').modal('show')
    },

    celebrated() {
      this.order.data.celebrated_at = moment().format('YYYY-MM-DD')
    }
  }
}
</script>
